import React from 'react'
import { useField, Field, ErrorMessage } from 'formik';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import styles from './index.module.scss'
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import TextError from './TextError'
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { FormControl, InputLabel, Box } from '@mui/material';

// --------------------TEXTFIELD

export const InputField = ({ label, showAsterik, ...props }) => {
    const [field, meta] = useField(props);

    return (
        <>
            <div className={styles.label}>
                <strong>
                    <label htmlFor={props.id || props.name}>
                        {label}{showAsterik && <span>*</span>}
                    </label>
                </strong>
            </div>

            <TextField
                {...field}
                {...props}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start" sx={{ color: '#0a48b3' }}>
                            {props.adornment}
                        </InputAdornment>
                    ),

                    endAdornment: (
                        <InputAdornment position="end">
                            {
                                props.endAdornment &&
                                <IconButton
                                    onClick={props.handleClickShowPassword}>
                                    {props.showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            }

                        </InputAdornment>
                    )
                }}
                sx={{
                    "& .MuiOutlinedInput-input": { padding: '12px' }
                }}
                autoComplete="new-password"
            />

        </>
    );
};


// --------------------CHECKBOX

export const CheckBoxField = ({ children, ...props }) => {
    const [field, meta] = useField({ ...props, type: 'checkbox' });

    return (
        <span>
            <label className="checkbox-input">
                <input type="checkbox" {...field} {...props} />
                {children}
            </label>
            {meta.touched && meta.error ? (
                <div className={styles.error}>{meta.error}</div>
            ) : null}
        </span>
    );
};


// --------------------CHECKBOX GROUP

export function CheckboxGroup(props) {
    const { name, options, ...rest } = props
    return (
        <>
            <Grid container spacing={2}>

                <Field name={name}>
                    {({ field, meta }) => {
                        return options.map(option => {
                            return (
                                <Grid item xs={12} md={4} key={option.key}>
                                    <Checkbox
                                        type='checkbox'
                                        id={option.value}
                                        {...field}
                                        {...rest}
                                        value={option.value}
                                        checked={field.value.includes(option.value)}
                                    />
                                    <label htmlFor={option.value}>{option.key}</label>
                                </Grid>
                            )
                        })
                    }}
                </Field>
            </Grid>

            <ErrorMessage component={TextError} name={name} />
        </>
    )
}


// --------------------RADIO GROUP

export function RadioGroup(props) {
    const { name, options, ...rest } = props
    return (
        <>
            <Grid container spacing={2}>
                <Field name={name}>
                    {({ field, meta }) => {
                        return options.map(option => {
                            return (
                                <Grid item xs={12} md={4} key={option.key}>
                                    <Radio
                                        type='radio'
                                        id={option.value}
                                        {...field}
                                        {...rest}
                                        value={option.value}
                                        checked={field.value === option.value}
                                    />
                                    <label htmlFor={option.value}>{option.key}</label>
                                </Grid>
                            )
                        })
                    }}
                </Field>
            </Grid>

            <ErrorMessage component={TextError} name={name} />
        </>
    )
}

// --------------------SELECT

export const SelectField = ({ label, ...props }) => {
    const [field, meta] = useField(props);

    return (
        <div>
            <div className={styles.label}>
                <strong>
                    <label htmlFor={props.id || props.name}>
                        {label}
                    </label>
                </strong>
            </div>
            <Select
                {...field}
                {...props}
                startAdornment={
                    <InputAdornment position="start" sx={{ color: '#0a48b3' }}>
                        {props.adornment}
                    </InputAdornment>
                }
                sx={{
                    "& .MuiOutlinedInput-input": { padding: '11px' }
                }}
            />
            {
                meta.touched && meta.error ? (
                    <div className={styles.error}>{meta.error}</div>
                ) : null
            }
        </div>
    );
};

export const PhoneInputField = ({ label = "Phone", showAsterik, ...props }) => {
    const [field, meta] = useField(props);
    const [focused, setFocused] = React.useState(false);
    const hasValue = field.value && field.value.length > 0;
    
    return (
        <FormControl fullWidth variant="outlined">
            <InputLabel
                shrink={focused || hasValue}
                sx={{
                    backgroundColor: 'white',
                    px: 0.5,
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                    }
                }}
            >
                {label}{showAsterik && <span style={{ color: 'red' }}> *</span>}
            </InputLabel>
            <Box sx={{ position: 'relative' }}>
                <PhoneInput
                    {...field}
                    {...props}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    containerStyle={{ width: "100%" }}
                    countryCodeEditable={false}
                    inputProps={{
                        name: 'phone',
                        required: props.required,
                        inputMode: 'numeric',
                    }}
                    inputStyle={{
                        color: "#00000099",
                        width: '100%',
                        height: '56px',
                        borderRadius: '4px',
                        border: focused ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.23)',
                        paddingLeft: '48px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s',
                    }}
                    buttonStyle={{
                        border: 'none',
                        background: 'transparent',
                    }}
                    containerClass="phone-input-container"
                    inputClass="phone-input-field"
                />
            </Box>
            {meta.touched && meta.error && (
                <div className={styles.error}>{meta.error}</div>
            )}
        </FormControl>
    )
}